import { ApiSubscriptionPackageOk } from '@type/v1-api-types';
import { useMemo, useCallback } from 'react';
import { useSwrPackages, useSwrMutationPaymentType } from '@hooks/useSwrApi/endpoints/subscription';
import { useAppDispatch, useAppSelector } from '@store/index';
import { saveSelectedUpgradePackage } from '@store/reducers/employer/subscription/subscriptionReducer';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { extractNumber } from '@utils/strings/extractNumber';
import { getObjectFromSessionStorage } from '@utils/form-helpers/session-storage';
import { showPaymentPackagesModal } from '@components/features/modals/employer/subscription/PaymentPackagesModal';


/**
 * @description This hook is used to handle the order process for not logged-in users. It checks if
 * there is a selected subscription in session storage and if the user is logged-in. If both conditions
 * are met, it tries to trigger an upgrade (if the subscription is available and not current).
 *
 * @param locale string; the locale
 * @param packagesFromServer ApiSubscriptionPackages; the subscription packages data from the server
 *
 * @returns isLoggedIn boolean; true if the user is logged-in
 * @returns tryToTriggerUpgrade () => void; function to trigger an upgrade
 */
export const useHandleNotAuthOrder = (
  locale = 'ro',
  packagesFromServer?: ApiSchema<'ActivationServices'>,
) => {
  // REDUX.
  // *****************************************
  const appDispatch = useAppDispatch();

  // Get the user data from the store.
  // If the user is not logged-in, the value is undefined.
  // *****************************************
  const isLoggedIn = Boolean(useAppSelector(getUserHeaderInfo));


  // SESSION STORAGE.
  // *****************************************
  // Check if there is a selected subscription in session storage.
  // We can only do this on the client side.
  // *****************************************
  const storageValue = useMemo(() => (typeof window !== 'undefined' ? getObjectFromSessionStorage('selectedSubscription') : {}), []);
  const storageSubscription = useMemo(
    () => (Object.keys(storageValue)?.length > 0 ? storageValue as ApiSubscriptionPackageOk : null),
    [storageValue],
  );


  // SWR HOOKS.
  // *****************************************
  // 1. If we have a selected subscription in session storage, we need the
  // subscription packages data if the user is logged in. We try to use data from server side
  // if we have it, otherwise we fetch it.
  // *****************************************
  const {
    data: packages,
  } = useSwrPackages(locale, packagesFromServer, Boolean(storageSubscription && isLoggedIn));

  // 2. POST data with the packageId to get the payment options
  // We save the response in Redux state from inside this hook
  // *****************************************
  const { trigger } = useSwrMutationPaymentType(locale, showPaymentPackagesModal);


  // TRY TRIGGER UPGRADE.
  // *****************************************
  // If we have a selected subscription in session storage and the user is logged in, we check if we
  // can trigger an upgrade. We always delete the selected subscription from session storage afterwards.
  // *****************************************
  const tryToTriggerUpgrade = useCallback(() => {
    if (storageSubscription && isLoggedIn) {
      // Match the selected subscription from session storage with the subscription packages.
      const subscription = packages?.subscriptionPackages?.find(
        (item) => item?.packageId === storageSubscription?.packageId,
      );

      // Check if we can trigger an upgrade.
      if (subscription?.available && !subscription?.current) {
        // the packageId is a string, but the API expects a number
        const packageId = extractNumber(subscription?.packageId);
        if (packageId) {
          appDispatch(saveSelectedUpgradePackage(subscription));
          void trigger({ packageId });
        }
      }

      // Remove the selected subscription from session storage.
      sessionStorage.removeItem('selectedSubscription');
    }
  }, [appDispatch, isLoggedIn, packages, storageSubscription, trigger]);


  // RETURN
  // *****************************************
  return {
    isLoggedIn,
    tryToTriggerUpgrade,
  };
};
